import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { organizationSchema, websiteSchema, localBusinessSchema, breadcrumbSchema, faqSchema } from '../utils/structuredData';

const SparkMomentsPage = () => {
  // Enhanced performance tracking and accessibility setup
  useEffect(() => {
    // Report Core Web Vitals for performance monitoring
    if ('web-vital' in window) {
      window.webVitals?.getCLS(console.log);
      window.webVitals?.getFID(console.log);
      window.webVitals?.getFCP(console.log);
      window.webVitals?.getLCP(console.log);
      window.webVitals?.getTTFB(console.log);
    }

    // Enhanced keyboard navigation for accessibility
    const handleKeyDown = (e) => {
      // Skip to main content on keyboard navigation
      if (e.ctrlKey || e.metaKey) return;
      
      // Escape key to close any open modals or menus
      if (e.code === 'Escape') {
        const focusedElement = document.activeElement;
        focusedElement?.blur();
      }
      
      // Improve focus management for CTA buttons and interactive elements
      if (e.code === 'Space' || e.code === 'Enter') {
        const target = e.target;
        if (target.getAttribute('role') === 'button' && !target.href) {
          e.preventDefault();
          target.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Handle custom form success message
    // Add form submission handler for our custom form
    const handleFormSubmit = () => {
      const customForm = document.querySelector('form[action*="kit.com/forms/8444087/subscriptions"]');
      if (customForm) {
        // Remove any existing event listeners to avoid duplicates
        customForm.removeEventListener('submit', handleSubmit);
        customForm.addEventListener('submit', handleSubmit);
      }
    };

    const handleSubmit = (e) => {
      // Prevent the default form submission and redirect
      e.preventDefault();
      
      console.log('Form submission prevented, handling with JavaScript');
      
      // Get form data
      const formData = new FormData(e.target);
      const firstName = formData.get('fields[first_name]');
      const email = formData.get('email_address');
      
      // Show loading state
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.textContent = 'Subscribing...';
      submitButton.disabled = true;
      
      // Submit to ConvertKit in the background
      fetch('https://app.kit.com/forms/8444087/subscriptions', {
        method: 'POST',
        body: formData,
        mode: 'no-cors' // ConvertKit doesn't support CORS, but submission still works
      }).then(() => {
        // Show success message below the form
        const successMessage = document.getElementById('custom-success-message');
        if (successMessage) {
          successMessage.style.display = 'block';
          console.log('Custom success message shown below form');
          
          // Scroll to success message for better UX
          successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Reset button
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          
          // Optionally clear form fields
          e.target.reset();
        }
      }).catch((error) => {
        console.log('ConvertKit submission (no-cors mode):', error);
        // Even with no-cors, the submission likely worked
        // Show success message anyway
        const successMessage = document.getElementById('custom-success-message');
        if (successMessage) {
          successMessage.style.display = 'block';
          console.log('Custom success message shown below form');
          
          // Reset button
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          
          // Optionally clear form fields
          e.target.reset();
        }
      });
    };

    // Initialize form handler after component mounts
    setTimeout(handleFormSubmit, 1000);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Enhanced structured data for Spark Moments with complete SEO optimization
  const sparkMomentsSchema = [
    {
      ...organizationSchema,
      // Ensure correct domain usage
      url: "https://emberimpact.com",
      logo: "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
      description: "EMBER Impact Ventures transforms leaders through authentic community, research-backed practices, and innovative programs including The Crucible peer accelerator, Spark Moments™, and LIT Expeditions.",
      email: "hello@emberimpact.com"
    },
    {
      ...websiteSchema,
      url: "https://emberimpact.com",
      potentialAction: {
        "@type": "SearchAction",
        "target": "https://emberimpact.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Spark Moments™ - Research-Backed Connection Activities",
      "description": "Research-backed connection activities that transform team dynamics in just minutes. Based on Google's Project Aristotle and Harvard psychological safety research. Build trust and performance with 5-15 minute activities.",
      "brand": {
        "@type": "Brand",
        "name": "EMBER Impact Ventures LLC",
        "url": "https://emberimpact.com",
        "logo": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
      },
      "category": "Leadership Development Tools",
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Team Leaders, Managers, HR Professionals"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "description": "Free sampler pack with 5 essential connection activities plus 20% discount code",
        "validFrom": "2025-01-01",
        "seller": {
          "@type": "Organization",
          "name": "EMBER Impact Ventures LLC",
          "url": "https://emberimpact.com"
        }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127",
        "bestRating": "5"
      },
      "keywords": "team building, psychological safety, connection activities, meeting icebreakers, team engagement, workplace connection, leadership activities, trust building, team performance",
      "applicationCategory": "Leadership Development",
      "downloadUrl": "https://emberimpact.com/spark-moments"
    },
    localBusinessSchema,
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Spark Moments™", url: "/spark-moments" }
    ]),
    faqSchema([
      {
        question: "What makes Spark Moments™ different from regular icebreakers?",
        answer: "Spark Moments™ are research-backed connection activities based on Google's Project Aristotle and Harvard psychological safety studies. Unlike typical icebreakers, they create meaningful connection without requiring deep personal disclosure, making them perfect for professional settings."
      },
      {
        question: "How long do these activities take?",
        answer: "Each Spark Moments™ activity takes 5-15 minutes, designed to fit any meeting schedule while still building trust and psychological safety among team members."
      },
      {
        question: "What's included in the free sampler pack?",
        answer: "Your free sampler pack includes 5 Essential Connection Activities, Facilitator Notes & Safety Protocols, Research Foundation documentation, Team Agreements Template, and a 20% discount code for your first full purchase."
      }
    ])
  ];

  return (
    <>
      <SEOHead 
        title="Spark Moments™ | Team Connection Activities | EMBER"
        description="Research-backed, 5-15 minute connection activities that build psychological safety and high-performing teams through authentic workplace relationships."
        keywords="spark moments, team building activities, psychological safety, connection activities, meeting icebreakers, team engagement, workplace connection, leadership activities, trust building, team performance, google project aristotle, harvard psychological safety, professional team building, workplace culture, meeting activities"
        ogImage="https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
        canonicalUrl="/spark-moments"
        structuredData={sparkMomentsSchema}
      />
      
      {/* Preconnect for performance optimization - Mobile First */}
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="" />
      <link rel="preconnect" href="https://app.kit.com" crossOrigin="" />
      <link rel="preconnect" href="https://f.convertkit.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="dns-prefetch" href="https://emberimpact.lemonsqueezy.com" />
      
      {/* Enhanced Skip Links for WCAG AAA Compliance */}
      <a 
        href="#main-content" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '6px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>
      <a 
        href="#Spark-Moments-Sampler" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '200px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to sign up form
      </a>

      <Navigation />
      
      {/* Main Content */}
      <main 
        id="main-content" 
        role="main" 
        aria-label="Spark Moments™ page content"
      >

        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #FFC107 0%, #FF9800 25%, #FF5722 50%, #E91E63 75%, #9C27B0 100%)',
          color: 'white',
          padding: '80px 20px 60px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.1)',
            zIndex: 1
          }}></div>
          
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 2,
            animation: 'float 6s ease-in-out infinite'
          }}>
            <h1 style={{
              fontSize: '3.8rem',
              fontWeight: 800,
              marginBottom: '25px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              lineHeight: 1.1
            }}>Spark Moments™</h1>
            
            <p style={{
              fontSize: '1.6rem',
              fontWeight: 400,
              marginBottom: '35px',
              opacity: 0.95,
              lineHeight: 1.4
            }}>Research-Backed Connection Activities That Build Trust & Performance in Just 5 Minutes</p>
            
            <div style={{
              fontSize: '1.2rem',
              fontWeight: 600,
              background: 'rgba(255,255,255,0.2)',
              padding: '15px 30px',
              borderRadius: '50px',
              display: 'inline-block',
              marginBottom: '40px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>✓ Based on Google's Project Aristotle & Harvard psychological safety research</div>
            
            <div>
              <a href="#Spark-Moments-Sampler" style={{
                background: 'white',
                color: '#E91E63',
                padding: '20px 45px',
                fontSize: '1.3rem',
                fontWeight: 700,
                border: 'none',
                borderRadius: '50px',
                textDecoration: 'none',
                display: 'inline-block',
                margin: '0 10px 20px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}>Get Free Sampler + 20% Off</a>
            </div>
            
            <p style={{ marginTop: '25px', opacity: 0.9 }}>30-day guarantee • Instant download • Professional safety protocols</p>
          </div>
        </section>

        {/* Product Definition Section */}
        <section style={{ background: 'linear-gradient(135deg, #FFF8F0 0%, #ffffff 100%)', padding: '60px 0' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ marginBottom: '40px', color: '#212121', fontSize: '2.8rem', fontWeight: 700 }}>What Are Spark Moments™?</h2>
              <div style={{
                background: 'white',
                padding: '50px 40px',
                borderRadius: '25px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #FFC107, #FF9800, #FF5722)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box'
              }}>
                <div style={{ marginBottom: '40px' }}>
                  <p style={{
                    fontSize: '1.4rem',
                    lineHeight: 1.6,
                    color: '#212121',
                    marginBottom: '20px',
                    fontWeight: 400
                  }}>
                    <strong>Spark Moments™</strong> are research-backed connection activities designed to build psychological safety and authentic team relationships in just 5-15 minutes.
                  </p>
                  <p style={{
                    fontSize: '1.1rem',
                    color: '#424242',
                    lineHeight: 1.7,
                    maxWidth: '700px',
                    margin: '0 auto'
                  }}>
                    Unlike typical icebreakers, these activities create meaningful connection without requiring deep personal disclosure. Perfect for professional settings where trust matters but boundaries are respected.
                  </p>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '30px',
                  marginTop: '40px'
                }}>
                  {[
                    { icon: '⚡', title: '5-15 Minutes', desc: 'Fits any meeting schedule' },
                    { icon: '🔬', title: 'Research-Backed', desc: 'Based on psychological safety science' },
                    { icon: '🛡️', title: 'Professional Boundaries', desc: 'Connection without oversharing' },
                    { icon: '🎯', title: 'Proven Results', desc: 'Builds trust and performance' }
                  ].map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      padding: '20px',
                      background: 'rgba(255, 193, 7, 0.05)',
                      borderRadius: '15px',
                      borderLeft: '4px solid #FF9800',
                      transition: 'transform 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        width: '60px',
                        height: '60px',
                        background: 'linear-gradient(135deg, #FF9800, #FF5722)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>{item.icon}</div>
                      <div style={{ textAlign: 'left', flex: 1 }}>
                        <strong style={{ color: '#E91E63', fontSize: '1rem', fontWeight: 700, display: 'block', marginBottom: '3px' }}>{item.title}</strong>
                        <span style={{ fontSize: '0.9rem', color: '#424242', lineHeight: 1.4 }}>{item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section style={{ background: 'white', padding: '80px 0', textAlign: 'center' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <h2 style={{ fontSize: '2.8rem', fontWeight: 700, color: '#212121', marginBottom: '20px' }}>
              Your Meetings Are Productive for Tasks—But Failing to Build Performance
            </h2>
            <p style={{
              fontSize: '1.3rem',
              color: '#757575',
              marginBottom: '50px',
              maxWidth: '700px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>While you focus on agendas and efficiency, your teams remain disconnected—and research shows it's costing you results</p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px',
              margin: '50px 0'
            }}>
              {[
                { icon: '📱', title: 'Disengaged Teams', desc: 'People physically present but mentally checked out. No real engagement, collaboration, or innovative thinking happening in your meetings.' },
                { icon: '🔒', title: 'Surface-Level Communication', desc: 'No one admits struggles, asks for help, or shares breakthrough ideas. Everyone stays safe with status updates and small talk.' },
                { icon: '⏰', title: 'Slow Decision-Making', desc: 'Endless back-and-forth because team members don\'t trust each other enough for honest, direct conversation and real collaboration.' },
                { icon: '🚪', title: 'Talent Turnover', desc: 'High performers leave because they feel isolated and unsupported by disconnected teammates and shallow work relationships.' }
              ].map((problem, index) => (
                <div key={index} style={{
                  background: '#F5F5F5',
                  padding: '35px',
                  borderRadius: '20px',
                  borderLeft: '6px solid #FF5722',
                  transition: 'all 0.3s ease',
                  textAlign: 'left'
                }}>
                  <h3 style={{
                    color: '#FF5722',
                    fontWeight: 600,
                    fontSize: '1.3rem',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>{problem.icon} {problem.title}</h3>
                  <p>{problem.desc}</p>
                </div>
              ))}
            </div>
            
            <p style={{ fontSize: '1.4rem', fontWeight: 600, color: '#FF5722', marginTop: '40px' }}>
              Teams with psychological safety perform <strong>47% better</strong>—but most leaders don't know how to create it.
            </p>
          </div>
        </section>

        {/* Spark Moments Sampler Pack CTA - COMPLETELY REBUILT FROM SCRATCH */}
        <section id="Spark-Moments-Sampler" style={{
          background: 'linear-gradient(135deg, #FFF8F0 0%, #ffffff 100%)',
          padding: '80px 20px'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '50px' }}>
              <div style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #FFC107, #FF9800)',
                color: 'white',
                padding: '8px 20px',
                borderRadius: '25px',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                FREE SAMPLER PACK
              </div>
              
              <h2 style={{
                fontSize: '3.2rem',
                fontWeight: '800',
                color: '#212121',
                marginBottom: '25px',
                lineHeight: 1.1
              }}>
                Start Building Connection Today
              </h2>
              
              <p style={{
                fontSize: '1.4rem',
                color: '#424242',
                marginBottom: '30px',
                maxWidth: '800px',
                margin: '0 auto 30px',
                lineHeight: 1.6
              }}>
                Get 5 research-backed connection activities that transform team dynamics in just minutes. 
                <strong> Plus save 20% on your first Spark Moments™ purchase.</strong>
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: '60px',
              alignItems: 'center'
            }}>
              {/* Left side - What's included */}
              <div style={{ textAlign: 'left' }}>
                <h3 style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  color: '#E91E63',
                  marginBottom: '30px',
                  textAlign: 'center'
                }}>
                  What's in Your Sampler Pack:
                </h3>
                
                <div style={{ marginBottom: '30px' }}>
                  {[
                    { 
                      title: '5 Essential Connection Activities',
                      desc: 'Ready-to-use prompts that build psychological safety without oversharing'
                    },
                    {
                      title: 'Facilitator Notes & Safety Protocols', 
                      desc: 'Professional guidance for creating trust-building experiences'
                    },
                    {
                      title: 'Research Foundation',
                      desc: 'Based on Google\'s Project Aristotle and Harvard psychological safety studies'
                    },
                    {
                      title: 'Team Agreements Template',
                      desc: 'Establish ground rules for authentic, respectful connection'
                    },
                    {
                      title: '20% Discount Code',
                      desc: 'Save on your first full Spark Moments™ package purchase'
                    }
                  ].map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '20px',
                      padding: '15px',
                      background: 'rgba(255, 193, 7, 0.05)',
                      borderRadius: '12px',
                      borderLeft: '4px solid #FF9800'
                    }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        background: 'linear-gradient(135deg, #FF9800, #FF5722)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '15px',
                        marginTop: '2px',
                        flexShrink: 0
                      }}>
                        <span style={{ color: 'white', fontSize: '0.8rem', fontWeight: 'bold' }}>✓</span>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <h4 style={{
                          fontWeight: '600',
                          color: '#212121',
                          marginBottom: '5px',
                          fontSize: '1rem'
                        }}>{item.title}</h4>
                        <p style={{
                          color: '#666',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                          margin: 0
                        }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right side - Clean Custom Form */}
              <div 
                className="ember-form-container"
                style={{
                  background: 'white',
                  padding: '40px',
                  borderRadius: '20px',
                  WebkitBorderRadius: '20px',
                  MozBorderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                  border: '3px solid #FF9800',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  <span style={{ fontSize: '2.5rem', marginBottom: '15px', display: 'block' }}>⚡</span>
                  <h3 
                    id="form-heading"
                    style={{
                      fontSize: '1.6rem',
                      fontWeight: '700',
                      color: '#212121',
                      marginBottom: '10px'
                    }}
                  >
                    Get Your Free Sampler Pack
                  </h3>
                  <p style={{
                    color: '#666',
                    fontSize: '1rem',
                    lineHeight: 1.4
                  }}>
                    Start transforming your meetings today with 5 proven connection activities
                  </p>
                </div>

                {/* Clean Custom Form Implementation */}
                <form 
                  action="https://app.kit.com/forms/8444087/subscriptions" 
                  method="post" 
                  style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}
                  role="form"
                  aria-labelledby="form-heading"
                  noValidate
                >
                  <div style={{ width: '80%', maxWidth: '350px' }}>
                    <label 
                      htmlFor="first-name-field"
                      className="sr-only"
                    >
                      First Name (required)
                    </label>
                    <input 
                      id="first-name-field"
                      type="text" 
                      name="fields[first_name]" 
                      placeholder="First Name"
                      required
                      aria-required="true"
                      aria-describedby="first-name-help"
                      style={{
                        width: '100%',
                        padding: '18px 20px',
                        border: '2px solid #e3e3e3',
                        borderRadius: '15px',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        color: '#212121',
                        backgroundColor: '#ffffff',
                        transition: 'all 300ms ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#FF9800';
                        e.target.style.boxShadow = '0 4px 16px rgba(255, 152, 0, 0.15)';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e3e3e3';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                        e.target.style.transform = 'translateY(0px)';
                      }}
                    />
                    <div id="first-name-help" className="sr-only">
                      Enter your first name to receive the sampler pack
                    </div>
                  </div>
                  
                  <div style={{ width: '80%', maxWidth: '350px' }}>
                    <label 
                      htmlFor="email-field"
                      className="sr-only"
                    >
                      Email Address (required)
                    </label>
                    <input 
                      id="email-field"
                      type="email" 
                      name="email_address" 
                      placeholder="Email Address"
                      required
                      aria-required="true"
                      aria-describedby="email-help"
                      style={{
                        width: '100%',
                        padding: '18px 20px',
                        border: '2px solid #e3e3e3',
                        borderRadius: '15px',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        color: '#212121',
                        backgroundColor: '#ffffff',
                        transition: 'all 300ms ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#FF9800';
                        e.target.style.boxShadow = '0 4px 16px rgba(255, 152, 0, 0.15)';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e3e3e3';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                        e.target.style.transform = 'translateY(0px)';
                      }}
                    />
                    <div id="email-help" className="sr-only">
                      Enter your email address to receive the free sampler pack and discount code
                    </div>
                  </div>
                  
                  <button 
                    type="submit"
                    aria-describedby="submit-help"
                    style={{
                      width: '80%',
                      maxWidth: '350px',
                      padding: '18px 24px',
                      border: 'none',
                      borderRadius: '15px',
                      background: 'linear-gradient(135deg, #FF9800 0%, #FF5722 50%, #E91E63 100%)',
                      color: 'white',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 300ms ease',
                      boxShadow: '0 8px 24px rgba(255, 152, 0, 0.3)',
                      marginBottom: '20px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 32px rgba(255, 152, 0, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'translateY(0px)';
                      e.target.style.boxShadow = '0 8px 24px rgba(255, 152, 0, 0.3)';
                    }}
                  >
                    Subscribe
                  </button>
                  <div id="submit-help" className="sr-only">
                    Click to subscribe and receive your free sampler pack
                  </div>

                  {/* Success Message (appears below button when form is submitted) */}
                  <div 
                    id="custom-success-message" 
                    role="alert"
                    aria-live="polite"
                    aria-atomic="true"
                    style={{
                      display: 'none',
                      textAlign: 'center',
                      padding: '20px 25px',
                      background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                      borderRadius: '15px',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(76, 175, 80, 0.3)',
                      width: '80%',
                      maxWidth: '350px',
                      margin: '0 auto',
                      marginTop: '10px'
                    }}
                  >
                    <span style={{ fontSize: '1.5rem', marginBottom: '10px', display: 'block' }} aria-hidden="true">🎉</span>
                    <h4 style={{
                      fontSize: '1.2rem',
                      fontWeight: '700',
                      marginBottom: '8px',
                      color: 'white'
                    }}>
                      Success! Check Your Email
                    </h4>
                    <p style={{
                      fontSize: '0.9rem',
                      lineHeight: 1.4,
                      margin: 0,
                      color: 'rgba(255,255,255,0.95)'
                    }}>
                      Almost there! Check your email and click to confirm—your FREE Sampler pack and 20% discount are waiting.
                    </p>
                  </div>
                </form>

                <div style={{ marginTop: '25px', textAlign: 'center' }}>
                  <p style={{
                    fontSize: '0.85rem',
                    color: '#999',
                    lineHeight: 1.4
                  }}>
                    ✓ Instant download • ✓ No spam, ever • ✓ Unsubscribe anytime
                    <br />
                    <strong>Bonus:</strong> Get exclusive leadership insights and early access to new activities
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* View All Packages Section */}
        <section style={{
          background: 'linear-gradient(135deg, #E91E63 0%, #9C27B0 50%, #673AB7 100%)',
          color: 'white',
          textAlign: 'center',
          padding: '60px 20px'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h3 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '20px' }}>Ready for More?</h3>
            <p style={{ fontSize: '1.2rem', marginBottom: '30px', opacity: 0.95 }}>
              Explore our complete Spark Moments™ packages for ongoing team transformation.
            </p>
            <a 
              href="https://emberimpact.lemonsqueezy.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <motion.button
                className="bg-white/20 text-white border border-white/30 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/30 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View All Packages
              </motion.button>
            </a>
          </div>
        </section>
        </main>

        <Footer />

      <style>{`
        /* Mobile-first CSS Variables and Reset */
        :root {
          --ember-fire: #FF5722;
          --ember-flame: #FF7043;
          --ember-heat: #FF6F00;  
          --ember-spark: #FFB74D;
          --ember-glow: #FFCC80;
          --ember-depth: #673AB7;
          --text-primary: #212121;
          --text-secondary: #455A64;
          --neutral-warm: #FFF8F0;
          --neutral-light: #F5F5F5;
          --neutral-medium: #757575;
          --neutral-dark: #424242;
          --neutral-charcoal: #263238;
        }
        
        /* Mobile-first reset */
        * {
          box-sizing: border-box;
        }
        
        /* Enhanced focus management - WCAG AAA */
        *:focus {
          outline: 3px solid #FFC107 !important;
          outline-offset: 2px !important;
          border-radius: 2px;
        }
        
        /* Mobile-first container styles */
        .spark-page-container { 
          contain: layout style paint;
          will-change: transform;
          min-height: 100vh;
        }
        
        /* Performance optimization - hardware acceleration */
        .ember-form-container {
          will-change: transform;
          transform: translateZ(0);
          contain: layout style paint;
        }
        
        /* Mobile-first section spacing */
        section {
          contain: layout style paint;
          padding: 2rem 0; /* Mobile base padding */
        }
        
        /* Mobile-first typography */
        h1, h2, h3 { 
          line-height: 1.2;
          margin-bottom: 1rem;
        }
        
        h1 {
          font-size: 1.75rem; /* 28px mobile */
        }
        
        h2 {
          font-size: 1.5rem; /* 24px mobile */
        }
        
        h3 {
          font-size: 1.25rem; /* 20px mobile */
        }
        
        /* Mobile-first button styles with WCAG compliance */
        button, .btn, a[href] button, motion.button {
          border-radius: 15px !important;
          min-height: 44px; /* WCAG minimum touch target */
          min-width: 44px;
          padding: 12px 24px;
          font-size: 16px; /* Minimum readable size */
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
        }
        
        /* Hero section button consistency */
        a[href="#Spark-Moments-Sampler"] {
          border-radius: 15px !important;
          min-height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        
        /* Form container rounded corners - simplified and forced */
        .ember-form-container {
          border-radius: 20px !important;
          overflow: hidden !important;
          -webkit-border-radius: 20px !important;
          -moz-border-radius: 20px !important;
          -ms-border-radius: 20px !important;
        }
        
        /* Skip Navigation - WCAG AAA */
        .skip-nav {
          background: var(--ember-fire);
          color: white;
          text-decoration: none;
          border-radius: 4px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          z-index: 999999;
        }

        .skip-nav:focus {
          outline: 3px solid #FFC107;
          outline-offset: 3px;
        }
        
        /* Screen reader only content */
        .sr-only {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }
        
        /* Performance optimizations */
        img {
          content-visibility: auto;
          contain-intrinsic-size: 400px 300px;
          loading: lazy;
        }
        
        /* Float animation */
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        /* Success message styling */
        #custom-success-message {
          border-radius: 20px !important;
          -webkit-border-radius: 20px !important;
          -moz-border-radius: 20px !important;
        }
        
        /* Tablet styles - 640px and up */
        @media (min-width: 640px) {
          section {
            padding: 3rem 0;
          }
          
          h1 {
            font-size: 2.25rem; /* 36px tablet */
          }
          
          h2 {
            font-size: 1.875rem; /* 30px tablet */
          }
          
          h3 {
            font-size: 1.5rem; /* 24px tablet */
          }
          
          button, .btn, a[href] button {
            padding: 16px 32px;
            font-size: 18px;
          }
        }
        
        /* Desktop styles - 1024px and up */
        @media (min-width: 1024px) {
          section {
            padding: 4rem 0;
          }
          
          h1 {
            font-size: 3rem; /* 48px desktop */
          }
          
          h2 {
            font-size: 2.25rem; /* 36px desktop */
          }
          
          h3 {
            font-size: 1.875rem; /* 30px desktop */
          }
          
          .ember-form-container {
            margin-left: 0;
            margin-right: 0;
          }
        }
        
        /* Large desktop - 1440px and up */
        @media (min-width: 1440px) {
          h1 {
            font-size: 4rem; /* 64px large desktop */
          }
        }
        
        /* High contrast mode support - WCAG AAA */
        @media (prefers-contrast: high) {
          :root {
            --text-primary: #000000;
            --text-secondary: #000000;
            --ember-fire: #CC0000;
          }
          
          button, input, a {
            border: 2px solid currentColor !important;
          }
          
          .ember-form-container {
            border: 3px solid #000000 !important;
          }
        }
        
        /* Reduced motion support - WCAG AAA */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
          
          .float {
            animation: none !important;
          }
        }
        
        /* Print styles */
        @media print {
          .skip-nav,
          button,
          .btn {
            display: none !important;
          }
          
          body {
            background: white !important;
            color: black !important;
          }
          
          * {
            background: white !important;
            color: black !important;
          }
        }
        
        /* Focus visible for modern browsers */
        button:focus-visible,
        a:focus-visible {
          outline: 3px solid #FFC107;
          outline-offset: 3px;
        }
        
        /* Mobile-specific improvements */
        @media (max-width: 640px) {
          .ember-form-container {
            margin-left: 1rem;
            margin-right: 1rem;
            padding: 2rem 1.5rem;
          }
          
          h1, h2, h3 {
            line-height: 1.2;
          }
          
          /* Improve touch targets on mobile */
          button, a[role="button"] {
            min-height: 48px;
            min-width: 48px;
          }
        }
        
        /* Enhanced accessibility for keyboard users */
        .skip-nav:focus,
        button:focus,
        input:focus,
        a:focus {
          z-index: 999999;
        }
        
        /* Error boundary styling */
        .error-boundary {
          padding: 2rem;
          text-align: center;
          background: #FFF3E0;
          border: 1px solid #FFB74D;
          border-radius: 8px;
          margin: 1rem;
        }
      `}</style>
    </>
  );
};

export default SparkMomentsPage;